import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RoleName } from 'core/constants/app-constants';
import { appRouterUrl } from 'core/constants/router-url';
import { requestIDPAuthToken, searchUsers } from 'core/services/user-service';
import {
  startViewAsUser,
  stopViewAsUser,
} from 'core/services/user-view-as-service';
import { extractUserId } from 'core/utils/data-utils';
import { formatLog } from 'core/utils/logger-utils';
import { resetHttpError, setHttpError } from './sharedSlice';
import { setStudentProfileCached } from './studentProfileSlice';

const initialState: UserProfileState = {
  emplid: null,
  roles: [],
  currentRole: null,
  viewAs: {
    userName: null,
    emplid: null,
    active: false,
    startPage: appRouterUrl.ADMIN_DASHBOARD,
    currentRole: undefined,
    loading: false,
    selectedUser: null,
  },
  search: {
    searchBy: '',
    loading: false,
    completed: false,
    data: [],
    selectedUser: null,
  },
};

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    setSearchLoading: (state, action: ActionOf<boolean>) => {
      state.search.loading = action.payload;
    },
    setSearchCompleted: (state, action: ActionOf<boolean>) => {
      state.search.completed = action.payload;
    },
    setViewAsLoading: (state, action: ActionOf<boolean>) => {
      state.viewAs.loading = action.payload;
    },
    setViewAsRole: (state, action: ActionOf<UserProfileRole>) => {
      state.viewAs.currentRole = action.payload;
    },
    setImpersonation: (state, action: ActionOf<Partial<UserViewAs>>) => {
      state.viewAs = {
        ...state.viewAs,
        ...action.payload,
      };
    },
    resumeUser: (state) => {
      state.viewAs = { ...initialState.viewAs };
      state.search = { ...initialState.search };
      stopViewAsUser();
    },
    setUserEmplid: (state, action: ActionOf<string>) => {
      state.emplid = action.payload;
    },
    setCurrentRole: (state, action: ActionOf<UserProfileRole>) => {
      state.currentRole = action.payload;
    },
    setPrimaryRole: (state, action: ActionOf<string[]>) => {
      const payload = action.payload;

      if (!payload || payload.length === 0) return;
      // For users with multiple roles pick the one with more privileges
      // Current users can still switch between their other roles
      if (payload.includes(RoleName.PLATFORM)) {
        state.currentRole = RoleName.PLATFORM;
      } else if (payload.includes(RoleName.ADMIN)) {
        state.currentRole = RoleName.ADMIN;
      } else if (payload.includes(RoleName.ADVISOR)) {
        state.currentRole = RoleName.ADVISOR;
      } else if (payload.includes(RoleName.COACH)) {
        state.currentRole = RoleName.COACH;
      } else {
        state.currentRole = payload[0] as UserProfileRole;
      }
    },
    setUserRoles: (state, action: ActionOf<string[]>) => {
      state.roles = action.payload;
    },
    setSearchBy: (state, action: ActionOf<string>) => {
      state.search.searchBy = action.payload;
    },
    setSearchData: (state, action: ActionOf<API.UserData.ProfileSearch[]>) => {
      state.search.data = action.payload;
    },
    setSelectedProfile: (
      state,
      action: ActionOf<API.UserData.ProfileSearch | null>,
    ) => {
      state.search.selectedUser = action.payload;
    },
  },
});

export const getSearchByAsync = createAsyncThunk(
  'userProfileSlice/getSearchByAsync',
  async (payload: { searchBy: string }, { dispatch, getState }) => {
    dispatch(resetHttpError());
    dispatch(setSearchLoading(true));
    dispatch(setSearchCompleted(false));

    try {
      const { searchBy } = payload;
      const users = await searchUsers(searchBy);

      dispatch(setSearchData(users));

      if (users.length === 1) {
        dispatch(setSelectedProfile(users[0]));
      }

      return users;
    } catch (error) {
      const res = error as HTTPError;
      dispatch(
        setHttpError({
          notAuthorized: res.response?.status === 404,
          httpError: (error as HTTPError).message,
          sourceAction: getSearchByAsync.typePrefix,
        }),
      );
      dispatch(setSearchData([]));
    } finally {
      dispatch(setSearchLoading(false));
      dispatch(setSearchCompleted(true));
    }
  },
);

export const setViewAsUserAsync = createAsyncThunk(
  'userProfileSlice/setViewAsUserAsync',
  async (
    payload: {
      userName: string;
      emplid: string;
      startPage: string;
    },
    { dispatch, getState },
  ) => {
    try {
      const state = getState() as AppState;
      const { selectedUser } = state.userProfile.search;
      const { userName, emplid, startPage } = payload;

      const realData = selectedUser?.profile?.realData;
      const incognitoUserName = extractUserId(
        realData?.email || '',
        realData?.asurite,
      );

      dispatch(setViewAsLoading(true));

      startViewAsUser(incognitoUserName || userName, emplid);

      const data = await requestIDPAuthToken();
      const defaultRole =
        data?.viewAsSubjectRole?.[0] || selectedUser?.roles?.[0] || '';

      dispatch(setStudentProfileCached(false));
      dispatch(
        setImpersonation({
          active: true,
          currentRole: defaultRole as UserProfileRole,
          userName,
          emplid,
          startPage,
          selectedUser,
        }),
      );

      console.log(
        ...formatLog({
          action: 'VIEW_AS_TOKEN',
          message: `Requested IDP Auth Token`,
        }),
      );

      return {
        success: true,
        data,
        viewAsDefaultRole: defaultRole,
      };
    } catch (error) {
      dispatch(
        setHttpError({
          httpError: (error as HTTPError).message,
          sourceAction: setViewAsUserAsync.typePrefix,
        }),
      );
    } finally {
      dispatch(setViewAsLoading(false));
    }
  },
);

export const {
  resumeUser,
  setUserRoles,
  setCurrentRole,
  setPrimaryRole,
  setViewAsLoading,
  setImpersonation,
  setViewAsRole,
  setSearchBy,
  setSearchLoading,
  setSearchCompleted,
  setSearchData,
  setSelectedProfile,
  setUserEmplid,
} = userProfileSlice.actions;

export default userProfileSlice.reducer;
